import type {
  Budget,
  BudgetCategoryId,
  ChildrenCategoryData,
  Family,
} from './schema';
import type { AnnualRate } from '~/landing/core/simulator/future-savings-planner';

import { dayjsDate } from '@polyseme/utils/date/date-utils';
import { ObjectUtils } from '@polyseme/utils/typescript/typescript-utils';

import {
  DECIMAL_PRECISION,
  MONTHS_IN_YEAR,
  PROJECTION_YEARS,
  budgetCategories,
  categories,
  familyBudgets,
} from './constants';

const calculateMonthlyRate = (annualRate: number): number => {
  if (annualRate <= -1) {
    throw new Error(
      'Annual rate must be greater than -1 to calculate monthly rate.'
    );
  }
  return Math.pow(1 + annualRate, 1 / MONTHS_IN_YEAR) - 1;
};

export const calculateTotalBudgetForAllCategories = (family: Family): number =>
  categories.reduce(
    (acc, category) =>
      acc +
      category.supplies.reduce((supplyAcc, supply) => {
        const count = family[category.id] || 0;
        return supplyAcc + supply.budget * count;
      }, 0),
    0
  );

export const calculateTotalBudgetForCategory = (
  category: ChildrenCategoryData,
  count: number
): number =>
  category.supplies.reduce((acc, supply) => {
    return acc + supply.budget * count;
  }, 0);

export const calculateTotalCashbackForAllCategories = (
  familyData: Family
): number =>
  categories.reduce((acc, category) => {
    return (
      acc +
      category.supplies.reduce((supplyAcc, supply) => {
        const count = familyData[category.id] || 0;
        const cashbackAmount = supply.budget * supply.cashback.percent;
        return supplyAcc + count * cashbackAmount;
      }, 0)
    );
  }, 0);

export const calculateFamilyBudget = (family: Family): Budget => {
  const totalChildren =
    family.middleHighSchoolers +
    family.schoolChildren +
    family.universityStudents;

  const budgetKey = Math.min(totalChildren, 4);

  return familyBudgets[budgetKey] || familyBudgets[4];
};

export const calculateSavingsPerBudgetCategory = (
  budget: Budget
): Record<BudgetCategoryId, number> => {
  return ObjectUtils.keys(budget).reduce(
    (acc, categoryId) => {
      const averagePercent =
        budgetCategories.find((category) => category.id === categoryId)
          ?.averageCashback.percent || 0;
      acc[categoryId] = budget[categoryId] * averagePercent;
      return acc;
    },
    {} as Record<BudgetCategoryId, number>
  );
};

export const simulateProjections = ({
  backToSchoolBudget,
  rate,
  yearlyBudget,
}: {
  backToSchoolBudget: number;
  rate: AnnualRate;
  yearlyBudget: Budget;
}) => {
  const savingsPerCategory = calculateSavingsPerBudgetCategory(yearlyBudget);
  const totalSavingsPerMonth =
    Object.values(savingsPerCategory).reduce((acc, value) => acc + value, 0) +
    backToSchoolBudget / MONTHS_IN_YEAR;

  const projections: {
    investmentMade: number;
    median: number;
    optimistic: number;
    pessimistic: number;
    year: number;
  }[] = [];
  const now = dayjsDate();
  const currentMonth = now.month() + 1;

  const endProjectionDate = now.add(PROJECTION_YEARS, 'year');
  const endProjectionMonth = endProjectionDate.month() + 1;

  let currentTotalSavings = 0;
  let medianBalance = 0;
  let pessimisticBalance = 0;
  let optimisticBalance = 0;

  const monthlyMedianRate = calculateMonthlyRate(rate.median);
  const monthlyPessimisticRate = calculateMonthlyRate(rate.min);
  const monthlyOptimisticRate = calculateMonthlyRate(rate.max);

  let currentIterationDate = now.startOf('month');

  for (let i = 0; i < PROJECTION_YEARS * MONTHS_IN_YEAR; i++) {
    const year = currentIterationDate.year();
    const month = currentIterationDate.month() + 1;

    currentTotalSavings += totalSavingsPerMonth;

    medianBalance += totalSavingsPerMonth;
    medianBalance *= 1 + monthlyMedianRate;

    pessimisticBalance += totalSavingsPerMonth;
    pessimisticBalance *= 1 + monthlyPessimisticRate;

    optimisticBalance += totalSavingsPerMonth;
    optimisticBalance *= 1 + monthlyOptimisticRate;

    const isEndOfProjectedYearCycle = month === currentMonth;
    const isLastMonthOfTotalProjection =
      year === endProjectionDate.year() &&
      month === endProjectionMonth &&
      i === PROJECTION_YEARS * MONTHS_IN_YEAR - 1;

    if (isEndOfProjectedYearCycle || isLastMonthOfTotalProjection) {
      const projectionYearIndex = Math.floor(i / MONTHS_IN_YEAR) + 1;
      if (projectionYearIndex <= PROJECTION_YEARS) {
        projections.push({
          year: currentIterationDate.year(),
          investmentMade: parseFloat(
            currentTotalSavings.toFixed(DECIMAL_PRECISION)
          ),
          median: parseFloat(medianBalance.toFixed(DECIMAL_PRECISION)),
          pessimistic: parseFloat(
            pessimisticBalance.toFixed(DECIMAL_PRECISION)
          ),
          optimistic: parseFloat(optimisticBalance.toFixed(DECIMAL_PRECISION)),
        });
      }
    }

    currentIterationDate = currentIterationDate.add(1, 'month');
  }

  return projections;
};
